import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api } from "../api";
import { Button } from "../components/ui/button";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export function BillingSuccess() {
  const [search] = useSearchParams();
  const nav = useNavigate();
  const sid = search.get("session_id");
  const [status, setStatus] = useState("pending");
  const [tries, setTries] = useState(0);

  useEffect(() => {
    if (!sid) return;
    let alive = true;
    const poll = async () => {
      try {
        const r = await api.get(`/payments/status/${sid}`);
        if (!alive) return;
        if (r.data.payment_status === "paid") {
          setStatus("paid");
        } else if (tries < 15) {
          setTimeout(() => setTries((t) => t + 1), 2000);
        } else {
          setStatus("timeout");
        }
      } catch {
        setStatus("error");
      }
    };
    poll();
    return () => { alive = false; };
  }, [sid, tries]);

  if (status === "paid") {
    return (
      <div className="min-h-screen grid place-items-center p-6 bg-slate-50">
        <div className="w-full max-w-md bg-white rounded-lg border border-slate-200 shadow-sm p-8 text-center" data-testid="billing-success">
          <CheckCircle2 className="mx-auto text-emerald-600" size={48} />
          <h1 className="mt-4 text-xl font-semibold text-slate-900">You're subscribed</h1>
          <p className="mt-2 text-sm text-slate-600">
            Thanks for subscribing. Your 14-day trial continues — you won't be charged until it ends, and you can cancel anytime from the billing portal.
          </p>
          <Button className="mt-6 w-full bg-blue-600 hover:bg-blue-700 text-white" onClick={() => nav("/")} data-testid="go-to-dashboard-btn">
            Go to dashboard
          </Button>
          <Link to="/billing" className="block mt-3 text-xs text-slate-500">View billing</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid place-items-center p-6 bg-slate-50">
      <div className="w-full max-w-md bg-white rounded-lg border border-slate-200 shadow-sm p-8 text-center" data-testid="billing-pending">
        <Loader2 className="mx-auto text-slate-500 animate-spin" size={44} />
        <h1 className="mt-4 text-lg font-semibold text-slate-900">Finalizing your subscription…</h1>
        <p className="mt-2 text-sm text-slate-600">This usually takes a few seconds.</p>
      </div>
    </div>
  );
}

export function BillingCancel() {
  const nav = useNavigate();
  return (
    <div className="min-h-screen grid place-items-center p-6 bg-slate-50">
      <div className="w-full max-w-md bg-white rounded-lg border border-slate-200 shadow-sm p-8 text-center" data-testid="billing-cancel">
        <XCircle className="mx-auto text-slate-400" size={48} />
        <h1 className="mt-4 text-xl font-semibold text-slate-900">Checkout canceled</h1>
        <p className="mt-2 text-sm text-slate-600">
          No charge was made. You can resume anytime from Billing.
        </p>
        <Button className="mt-6 w-full bg-blue-600 hover:bg-blue-700 text-white" onClick={() => nav("/billing")} data-testid="back-to-billing-btn">
          Back to billing
        </Button>
      </div>
    </div>
  );
}
